/**
 * Canvas helpers for the identity-photo crop.
 *
 * The full-resolution file is already in the browser, so the crop is taken
 * from the original pixels — the server only ever sees a downscaled copy and
 * only ever returns coordinates.
 */

/** What the vision model works at; sending more is wasted upload and tokens. */
const MODEL_IMAGE_MAX_EDGE_PX = 1568

type Rect = { x: number; y: number; width: number; height: number }

/** Fractions of the image, 0–1. */
export type NormalizedBox = Rect

function loadImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(image)
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Could not read the image'))
    }
    image.src = url
  })
}

/** `source` is in pixels; `width`/`height` size the output canvas. */
function draw(image: HTMLImageElement, source: Rect, width: number, height: number) {
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(width))
  canvas.height = Math.max(1, Math.round(height))

  const context = canvas.getContext('2d')
  if (!context) throw new Error('Canvas is unavailable')
  context.drawImage(
    image,
    source.x,
    source.y,
    source.width,
    source.height,
    0,
    0,
    canvas.width,
    canvas.height,
  )
  return canvas
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not encode the image'))),
      type,
      quality,
    )
  })
}

/** Natural pixel dimensions of the image. */
export async function imageDimensions(file: Blob): Promise<{ width: number; height: number }> {
  const image = await loadImage(file)
  return { width: image.width, height: image.height }
}

/** Base64 (no data-URL prefix) of the image downscaled to the model's working size. */
export async function toDownscaledBase64(file: File): Promise<string> {
  const image = await loadImage(file)
  const scale = Math.min(1, MODEL_IMAGE_MAX_EDGE_PX / Math.max(image.width, image.height))
  const canvas = draw(
    image,
    { x: 0, y: 0, width: image.width, height: image.height },
    image.width * scale,
    image.height * scale,
  )
  return canvas.toDataURL('image/jpeg', 0.9).split(',')[1]
}

/** Crops `box` (fractions of the image) out of `file` at full resolution. */
export async function cropToBlob(file: File, box: NormalizedBox): Promise<Blob> {
  const image = await loadImage(file)
  const source = {
    x: box.x * image.width,
    y: box.y * image.height,
    width: box.width * image.width,
    height: box.height * image.height,
  }
  return toBlob(draw(image, source, source.width, source.height), 'image/jpeg', 0.92)
}
